/**
 * Tiny module-level mirror of the CURRENT session's tenant role (RD-AUD-013 —
 * operations money-blindness, client wiring).
 *
 * WHY THIS EXISTS: `role` normally lives only in React state (`AuthContext`),
 * which plain (non-React) modules — services, data-loading helpers — cannot
 * read. Threading a `role` param through every call site of a shared read
 * (`getJobs`, `getJobsForCustomerDateRange`, the job-detail loaders) risks the
 * exact footgun this repo has hit before under other names: a hand-threaded
 * key/param list that's easy to miss at ONE call site, which then silently
 * empties (or, worse, error-screens) a real screen for the `operations` role
 * with no test failure and no visible signal (see CLAUDE.md's anti-patterns
 * section on hand-threaded call-site lists). Auto-detecting the role from a
 * single mirrored source removes that failure mode entirely — every current
 * AND future caller of a role-aware read gets the right table/view for free.
 *
 * SOURCE OF TRUTH: mirrors `AuthContext`'s resolved `role` state value
 * verbatim — this module never re-derives role from the JWT or anywhere else
 * independently, so there is exactly ONE place that decides "what role is
 * this session," not two that can drift apart. `AuthContext` calls
 * `setCurrentTenantRole` at every site it calls `setRole` (sign-in, sign-out,
 * role-drift resync) — see `src/AuthContext.js`.
 *
 * Read-side callers should treat `null` as "not operations" (i.e. behave
 * exactly as they did before this store existed) — it means either signed out
 * or role not yet resolved; fail open to the pre-existing base-table
 * behavior, never fail closed into a view a non-operations caller can't use.
 */

let currentTenantRole = null;

/** Called by AuthContext whenever the resolved session role changes. */
export function setCurrentTenantRole(role) {
  currentTenantRole = typeof role === 'string' && role ? role : null;
}

/** Returns the mirrored role string, or null if unknown/signed out. */
export function getCurrentTenantRole() {
  return currentTenantRole;
}

/** Convenience predicate — the one check every fork in this wave needs. */
export function isOperationsSession() {
  return currentTenantRole === 'operations';
}
